import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

export default class extends Controller {
  static targets = ["anchor", "floatingCta", "lockTooltip", "optionTemplate", "confirmModal", "confirmTitle", "confirmBody"];

  // --- Floating "Add custom fields" CTA ---
  // Shown until the user reaches the bottom of the page (by clicking it or scrolling there).
  // Server hides it entirely when custom fields already exist.

  connect() {
    this.scrollContainer = this.element.querySelector(".referral-form-config-scroll");
    if (this.scrollContainer) {
      this.onScroll = this.onScroll.bind(this);
      this.scrollContainer.addEventListener("scroll", this.onScroll, { passive: true });
    }
  }

  disconnect() {
    this.scrollContainer?.removeEventListener("scroll", this.onScroll);
  }

  scrollToCustomFields() {
    this.scrollContainer?.scrollTo({ top: this.scrollContainer.scrollHeight, behavior: "smooth" });
  }

  onScroll() {
    if (!this.hasFloatingCtaTarget || !this.scrollContainer) return;
    const el = this.scrollContainer;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) this.floatingCtaTarget.remove();
  }

  // --- "Standard fields cannot be modified" tooltip following the cursor ---

  // The "Patient is not the subscriber" checkbox is interactive in the editor preview.
  toggleSubscriber(event) {
    const fields = document.getElementById("standard-subscriber-fields");
    if (fields) fields.classList.toggle("d-none", !event.target.checked);
  }

  moveLockTooltip(event) {
    if (!this.hasLockTooltipTarget) return;
    // No "cannot be modified" hint over interactive controls or configurable sections
    // (Preferences / Insurance, which have their own toggles).
    if (event.target.closest(".rfc-pref-toggle, .rfc-editable, .rfc-configurable")) {
      this.lockTooltipTarget.classList.add("d-none");
      return;
    }
    const wrapper = event.currentTarget.getBoundingClientRect();
    this.lockTooltipTarget.classList.remove("d-none");
    this.lockTooltipTarget.style.left = `${event.clientX - wrapper.left}px`;
    this.lockTooltipTarget.style.top = `${event.clientY - wrapper.top - 36}px`;
  }

  hideLockTooltip() {
    if (this.hasLockTooltipTarget) this.lockTooltipTarget.classList.add("d-none");
  }

  // --- Sections ---

  // Optional data-template on the clicked item creates a template-prefilled section.
  addSection(event) {
    const template = event.currentTarget?.dataset.template;
    this.request("/admin/referral_form_configs/add_section", {
      method: "POST",
      params: template ? { template } : {},
    });
  }

  editSectionTitle(event) {
    const section = event.target.closest(".referral-form-config-section");
    section.querySelector(".rfc-section-title-preview").classList.add("d-none");
    const edit = section.querySelector(".rfc-section-title-edit");
    edit.classList.remove("d-none");
    const input = edit.querySelector(".rfc-section-title-value");
    input.focus();
    input.select();
  }

  cancelSectionTitle(event) {
    const section = event.target.closest(".referral-form-config-section");
    const input = section.querySelector(".rfc-section-title-value");
    input.value = input.dataset.originalTitle ?? input.defaultValue;
    section.querySelector(".rfc-section-title-edit").classList.add("d-none");
    section.querySelector(".rfc-section-title-preview").classList.remove("d-none");
  }

  moveSection(event) {
    const section = event.target.closest(".referral-form-config-section");
    const direction = event.currentTarget.dataset.direction;
    const sibling = direction === "up" ? section.previousElementSibling : section.nextElementSibling;
    if (!sibling || !sibling.classList.contains("referral-form-config-section")) return;

    if (direction === "up") sibling.insertAdjacentElement("beforebegin", section);
    else sibling.insertAdjacentElement("afterend", section);

    this.request("/admin/referral_form_configs/move_section", {
      method: "POST",
      params: { section_key: section.dataset.sectionKey, direction },
    });
  }

  // Gutter toggle for an org preference that gates a standard field (e.g. provider preference).
  togglePreference(event) {
    const toggle = event.currentTarget;
    const isOn = toggle.classList.toggle("is-on");
    const field = document.getElementById(toggle.dataset.field);
    if (field) field.classList.toggle("d-none", !isOn);

    // Optional separate header to keep in sync — shown while this field OR a sibling field is visible.
    if (toggle.dataset.header) {
      const header = document.getElementById(toggle.dataset.header);
      const sibling = toggle.dataset.sibling ? document.getElementById(toggle.dataset.sibling) : null;
      const siblingVisible = sibling && !sibling.classList.contains("d-none");
      if (header) header.classList.toggle("d-none", !isOn && !siblingVisible);
    }

    this.request("/admin/referral_form_configs/update_preference", {
      method: "POST",
      params: { preference: toggle.dataset.preference, value: isOn },
    });
  }

  deleteSection(event) {
    const section = event.target.closest(".referral-form-config-section");
    const hasFields = section.querySelector(".config-field-item") !== null;
    this.promptDelete({
      title: "Delete section?",
      body: hasFields
        ? "This section and all of its fields will be permanently removed."
        : "This section will be permanently removed.",
      url: "/admin/referral_form_configs/destroy_section",
      params: { section_key: section.dataset.sectionKey },
    });
  }

  // --- Fields ---

  addField(event) {
    const item = event.currentTarget;
    this.request("/admin/referral_form_configs/add_field", {
      method: "POST",
      params: { section_key: item.dataset.sectionKey, field_type: item.dataset.fieldType },
    });
  }

  editField(event) {
    this.request("/admin/referral_form_configs/edit_field", {
      method: "GET",
      params: this.fieldParams(event),
    });
  }

  cancelField(event) {
    this.request("/admin/referral_form_configs/render_field", {
      method: "GET",
      params: this.fieldParams(event),
    });
  }

  deleteField(event) {
    this.promptDelete({
      title: "Delete field?",
      body: "This field will be permanently removed.",
      url: "/admin/referral_form_configs/destroy_field",
      params: this.fieldParams(event),
    });
  }

  // --- Confirm-delete modal ---

  promptDelete({ title, body, url, params }) {
    this.pendingDelete = { url, params };
    this.confirmTitleTarget.textContent = title;
    this.confirmBodyTarget.textContent = body;
    Modal.getOrCreateInstance(this.confirmModalTarget).show();
  }

  confirmDelete() {
    const pending = this.pendingDelete;
    this.pendingDelete = null;
    Modal.getInstance(this.confirmModalTarget)?.hide();
    if (!pending) return;

    this.request(pending.url, { method: "DELETE", params: pending.params });
  }

  duplicateField(event) {
    this.request("/admin/referral_form_configs/duplicate_field", {
      method: "POST",
      params: this.fieldParams(event),
    });
  }

  // Reorder a field from its preview (persists immediately, unlike the editor card).
  moveField(event) {
    const item = event.target.closest(".config-field-item");
    this.request("/admin/referral_form_configs/move_field", {
      method: "POST",
      params: {
        section_key: item.dataset.sectionKey,
        field_key: item.dataset.fieldKey,
        direction: event.currentTarget.dataset.direction,
      },
    });
  }

  fieldParams(event) {
    const item = event.target.closest(".config-field-item");
    return { section_key: item.dataset.sectionKey, field_key: item.dataset.fieldKey };
  }

  // --- Options within the field editor ---

  addNewOption(event) {
    const card = event.target.closest(".config-field-item");
    const optionsSection = card.querySelector(".options-section");
    const newOption = this.optionTemplateTargets
      .find((template) => card.contains(template))
      .content.firstElementChild.cloneNode(true);
    optionsSection.appendChild(newOption);
    newOption.querySelector("input[type=text]").focus();
  }

  deleteOption(event) {
    event.target.closest(".option").remove();
  }

  // --- Reordering (within the editor card; persisted on Save) ---

  orderFields(event) {
    const card = event.target.closest(".config-field-item");
    const container = card.closest(".referral-form-config-section-fields");
    const items = Array.from(container.querySelectorAll(".config-field-item"));
    const index = items.indexOf(card);
    const direction = event.target.dataset.orderDirection;

    let newIndex = index;
    if (direction === "up") newIndex = index - 1;
    if (direction === "down") newIndex = index + 1;
    if (direction === "first") newIndex = 0;
    if (direction === "last") newIndex = items.length - 1;
    newIndex = Math.max(0, Math.min(items.length - 1, newIndex));
    if (newIndex === index) return;

    const reference = items[newIndex];
    if (newIndex < index) reference.insertAdjacentElement("beforebegin", card);
    else reference.insertAdjacentElement("afterend", card);

    const orderInput = card.querySelector('input[name="field[order]"]');
    if (orderInput) orderInput.value = newIndex + 1;
  }

  // --- Helpers ---

  async request(url, { method = "GET", params = {} } = {}) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
        Accept: "text/vnd.turbo-stream.html",
      },
    };

    if (method === "GET") {
      const query = new URLSearchParams(params).toString();
      if (query) url = `${url}?${query}`;
    } else {
      const formData = new FormData();
      Object.entries(params).forEach(([key, value]) => formData.append(key, value));
      options.body = formData;
    }

    const response = await fetch(url, options);
    const html = await response.text();
    if (html) Turbo.renderStreamMessage(html);
  }
}
